import { AccessTime, CalendarMonth } from "@mui/icons-material";
import { Dialog, DialogTitle, IconButton, InputAdornment, TextField, TextFieldProps } from "@mui/material";
import { StaticDatePicker, StaticTimePicker } from "@mui/x-date-pickers";
import { deDE, enUS, esES, frFR, itIT, ptBR } from "@mui/x-date-pickers/locales";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/fr";
import "dayjs/locale/pt";
import { getTranslation } from "common";
import { useTranslations, useUser } from "hooks";
import React, { useState } from "react";

type Props = Omit<TextFieldProps, "value" | "type" | "onChange"> & {
  type: "date" | "time";
  value: string;
  onChange: (value: string) => void;
};

const locales = { it: itIT, en: enUS, de: deDE, es: esES, fr: frFR, pt: ptBR };

export default function CalendarPickerField({ type, value, onChange, disabled, inputProps, ...props }: Props) {
  const translations = useTranslations();
  const user = useUser();
  const [draft, setDraft] = useState<Dayjs | null>(null);
  const [open, setOpen] = useState(false);
  const label = inputProps?.["aria-label"];
  const fieldId = inputProps?.["data-calendar-field-id"];
  const language = (user?.i18n || navigator.language).split("-")[0];
  const locale = locales[language as keyof typeof locales] || enUS;
  const openPicker = () => {
    if (disabled) return;
    const parsed = type === "time"
      ? dayjs(`2000-01-01T${value}`)
      : dayjs(value).year(Number(value.slice(0, 4)));
    setDraft(parsed.isValid() ? parsed : dayjs().startOf("day"));
    setOpen(true);
  };
  const pickerProps = {
    value: draft,
    onChange: setDraft,
    onAccept: (selected: Dayjs | null) => {
      if (selected?.isValid() && !disabled) {
        onChange(selected.format(type === "time" ? "HH:mm" : "YYYY-MM-DD"));
        setOpen(false);
      }
    },
    onClose: () => setOpen(false),
    localeText: {
      ...locale.components.MuiLocalizationProvider.defaultProps.localeText,
      okButtonLabel: getTranslation(translations, "pm.button.save"),
      cancelButtonLabel: getTranslation(translations, "user.buttoncancel.label"),
    },
  };

  return <>
    <TextField
      {...props}
      type={type}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      inputProps={{ ...inputProps, onClick: openPicker }}
      sx={{ "& input::-webkit-calendar-picker-indicator": { display: "none" } }}
      InputProps={{ endAdornment: <InputAdornment position="end">
        <IconButton disabled={disabled} aria-label={label} aria-haspopup="dialog"
          data-testid={`calendar-picker-${fieldId}`} onClick={openPicker} edge="end">
          {type === "time" ? <AccessTime /> : <CalendarMonth />}
        </IconButton>
      </InputAdornment> }}
    />
    <Dialog open={open && !disabled} onClose={() => setOpen(false)} maxWidth="xs"
      aria-labelledby={`calendar-picker-title-${fieldId}`} data-testid="calendar-field-picker">
      <DialogTitle id={`calendar-picker-title-${fieldId}`}>{label}</DialogTitle>
      {type === "time"
        ? <StaticTimePicker {...pickerProps} ampm={false} views={["hours", "minutes"]} minutesStep={1} />
        : <StaticDatePicker {...pickerProps} views={["year", "month", "day"]} openTo="day" />}
    </Dialog>
  </>;
}
